import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  CalendarCheck,
  BookOpen,
  FileText,
  Bell,
  GraduationCap,
  Award,
  ClipboardList,
  Clock,
  CalendarOff,
  Library,
  Building2,
  BookMarked,
  BarChart3,
  BarChart2,
  Bot,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Menu,
  type LucideIcon,
} from 'lucide-react';
import { useLang } from '../lib/lang';
import { clearTokens, primaryRoleLabel, type AuthUser } from '../lib/auth';
import { getNavCapabilities, primaryRole, schoolIdFromUser, ROLE_LABELS } from '../lib/rbac';

// ── Shell context ────────────────────────────────────────────────────────────

interface ShellCtx {
  setPageTitle: (title: string) => void;
  collapsed: boolean;
}
const ShellContext = createContext<ShellCtx>({
  setPageTitle: () => {},
  collapsed: false,
});
export function useShell(): ShellCtx { return useContext(ShellContext); }

// ── Nav definition ────────────────────────────────────────────────────────────

interface NavItem {
  path:    string;
  labelMs: string;
  labelEn: string;
  Icon:    LucideIcon;
  /** If true, only show when the capability flag is truthy. */
  cap?: keyof ReturnType<typeof getNavCapabilities>;
}
interface NavGroup {
  groupMs: string;
  groupEn: string;
  /** Cap guard — hide the whole group if this flag is false */
  cap?: keyof ReturnType<typeof getNavCapabilities>;
  items: NavItem[];
}

const ALL_NAV: NavGroup[] = [
  {
    groupMs: 'PAPAN PEMUKA',
    groupEn: 'OVERVIEW',
    items: [
      { path: '/', labelMs: 'Papan Pemuka', labelEn: 'Dashboard', Icon: LayoutDashboard },
    ],
  },
  {
    groupMs: 'PELAJAR',
    groupEn: 'STUDENTS',
    cap: 'showStudents',
    items: [
      { path: '/admin/attendance',        labelMs: 'Kehadiran',          labelEn: 'Attendance',       Icon: CalendarCheck },
      { path: '/admin/grades',            labelMs: 'Gred',               labelEn: 'Grades',           Icon: BookOpen },
      // Applications: admin/MOE only — teachers & parents have NONE (tender matrix)
      { path: '/admin/applications',      labelMs: 'Permohonan',         labelEn: 'Applications',     Icon: FileText,   cap: 'showApplications' },
      // Alerts: all staff + parents (parents see their child's absence alerts)
      { path: '/admin/attendance/alerts', labelMs: 'Makluman Kehadiran', labelEn: 'Absence Alerts',   Icon: Bell,       cap: 'showAlerts' },
    ],
  },
  {
    groupMs: 'PENDIDIK',
    groupEn: 'EDUCATORS',
    cap: 'showEducators',
    items: [
      { path: '/admin/educators',            labelMs: 'Pendidik',    labelEn: 'Educators',  Icon: GraduationCap },
      { path: '/admin/cpd',                  labelMs: 'CPD',         labelEn: 'CPD',        Icon: Award },
      { path: '/admin/tpa',                  labelMs: 'TPA',         labelEn: 'TPA',        Icon: ClipboardList },
      { path: '/admin/timetable',            labelMs: 'Jadual',      labelEn: 'Timetable',  Icon: Clock,         cap: 'showTimetable' },
      { path: '/admin/timetable/substitute', labelMs: 'Penggantian', labelEn: 'Substitute', Icon: CalendarOff,   cap: 'showSubstitute' },
    ],
  },
  {
    groupMs: 'PERPUSTAKAAN',
    groupEn: 'LIBRARY',
    cap: 'showLibrary',
    items: [
      { path: '/admin/library',              labelMs: 'Katalog',    labelEn: 'Catalogue',     Icon: Library },
      { path: '/admin/library/loans',        labelMs: 'Pinjaman',   labelEn: 'Loans',         Icon: BookMarked,    cap: 'showLibraryAdmin' },
      { path: '/admin/library/reservations', labelMs: 'Tempahan',   labelEn: 'Reservations',  Icon: ClipboardList, cap: 'showLibraryAdmin' },
    ],
  },
  {
    groupMs: 'JABATAN PENDIDIKAN SWASTA',
    groupEn: 'PRIVATE EDUCATION (DPE)',
    cap: 'showDpe',
    items: [
      { path: '/admin/dpe',             labelMs: 'Sekolah Swasta', labelEn: 'Private Schools', Icon: Building2 },
      { path: '/admin/dpe/inspections', labelMs: 'Pemeriksaan',    labelEn: 'Inspections',     Icon: ClipboardList },
      { path: '/admin/dpe/approvals',   labelMs: 'Kelulusan Guru', labelEn: 'Teacher Approvals',Icon: GraduationCap },
      { path: '/admin/dpe/bulletins',   labelMs: 'Buletin',        labelEn: 'Bulletins',       Icon: Bell },
    ],
  },
  {
    groupMs: 'KEMENTERIAN AGAMA (MORA)',
    groupEn: 'RELIGIOUS AFFAIRS (MORA)',
    cap: 'showMora',
    items: [
      { path: '/admin/mora', labelMs: 'Sekolah MORA', labelEn: 'MORA Schools', Icon: BookMarked },
    ],
  },
  {
    groupMs: 'LAPORAN & ANALITIK',
    groupEn: 'REPORTS & ANALYTICS',
    cap: 'showReports',
    items: [
      { path: '/admin/reports',       labelMs: 'Laporan',   labelEn: 'Reports',       Icon: BarChart3 },
      { path: '/admin/moe-dashboard', labelMs: 'Papan KPM', labelEn: 'MOE Dashboard', Icon: BarChart2, cap: 'showMoeDashboard' },
    ],
  },
  {
    groupMs: 'SISTEM',
    groupEn: 'SYSTEM',
    items: [
      { path: '/admin/edubot',        labelMs: 'EduBot',       labelEn: 'EduBot',        Icon: Bot },
      { path: '/admin/demo-controls', labelMs: 'Kawalan Demo', labelEn: 'Demo Controls', Icon: Settings, cap: 'showDemoControls' },
    ],
  },
];

// ── Colour constants (self-contained, no token import needed in this file) ──

const C = {
  sidebar:      '#1A1A1A',
  sidebarBorder:'#2D2D2D',
  yellow:       '#F7D000',
  yellowDark:   '#D4A800',
  white:        '#FFFFFF',
  gray100:      '#F5F5F5',
  gray200:      '#EEEEEE',
  gray400:      '#BDBDBD',
  gray600:      '#757575',
  black:        '#1A1A1A',
  canvas:       '#F9FAFB',
  navbarBorder: '#E5E7EB',
  navbarBg:     '#FFFFFF',
  activeItemBg: 'rgba(247,208,0,0.12)',
  hoverItemBg:  'rgba(255,255,255,0.06)',
};

// ── Sidebar ───────────────────────────────────────────────────────────────────

function Sidebar({
  user,
  onLogout,
  collapsed,
  onToggle,
  mobileOpen,
  onMobileClose,
}: {
  user: AuthUser;
  onLogout: () => void;
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
}) {
  const { lang } = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const caps = getNavCapabilities(user);
  const role = primaryRole(user);
  const roleLabel = ROLE_LABELS[role];

  function isActive(path: string) {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  }

  function handleNav(path: string) {
    navigate(path);
    onMobileClose();
  }

  const sidebarW = collapsed ? '64px' : '240px';

  const sidebarStyle: React.CSSProperties = {
    position:        'fixed',
    top:             0,
    left:            0,
    bottom:          0,
    width:           sidebarW,
    backgroundColor: C.sidebar,
    borderRight:     `1px solid ${C.sidebarBorder}`,
    display:         'flex',
    flexDirection:   'column',
    transition:      'width 0.2s ease',
    overflowX:       'hidden',
    overflowY:       'auto',
    zIndex:          300,
    // Mobile: hidden by default, show when mobileOpen
  };

  // Mobile overlay
  const overlayStyle: React.CSSProperties = {
    position:   'fixed',
    inset:      0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    backdropFilter:  'blur(2px)',
    zIndex:     299,
    display:    mobileOpen ? 'block' : 'none',
  };

  return (
    <>
      {/* Mobile backdrop */}
      <div style={overlayStyle} onClick={onMobileClose} aria-hidden />

      <nav
        style={{
          ...sidebarStyle,
          // On mobile, use transform to slide
          transform: mobileOpen ? 'translateX(0)' : undefined,
        }}
        aria-label="Navigasi utama / Main navigation"
      >
        {/* Logo area */}
        <div
          style={{
            padding:        collapsed ? '20px 0' : '20px 16px',
            display:        'flex',
            alignItems:     'center',
            justifyContent: collapsed ? 'center' : 'space-between',
            borderBottom:   `1px solid ${C.sidebarBorder}`,
            minHeight:      '64px',
            flexShrink:     0,
          }}
        >
          {!collapsed && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
              <div
                style={{
                  width:           '32px',
                  height:          '32px',
                  backgroundColor: C.yellow,
                  borderRadius:    '8px',
                  display:         'flex',
                  alignItems:      'center',
                  justifyContent:  'center',
                  fontWeight:      700,
                  fontSize:        '16px',
                  color:           C.black,
                  flexShrink:      0,
                }}
              >
                S
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: C.white, fontWeight: 700, fontSize: '15px', lineHeight: 1.2 }}>
                  SERPS
                </div>
                <div
                  style={{
                    color:     C.gray400,
                    fontSize:  '10px',
                    lineHeight: 1.3,
                    overflow:  'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {lang === 'en' ? 'School Records System' : 'Sistem Rekod Sekolah'}
                </div>
              </div>
            </div>
          )}

          {collapsed && (
            <div
              style={{
                width:           '32px',
                height:          '32px',
                backgroundColor: C.yellow,
                borderRadius:    '8px',
                display:         'flex',
                alignItems:      'center',
                justifyContent:  'center',
                fontWeight:      700,
                fontSize:        '16px',
                color:           C.black,
              }}
            >
              S
            </div>
          )}

          {!collapsed && (
            <button
              onClick={onToggle}
              aria-label="Runtuhkan bar sisi / Collapse sidebar"
              style={iconButtonStyle}
            >
              <ChevronLeft size={16} strokeWidth={2} />
            </button>
          )}
        </div>

        {/* Nav groups */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0', paddingBottom: '16px' }}>
          {ALL_NAV.map((group) => {
            // Hide whole group if capability flag is false
            if (group.cap && !caps[group.cap]) return null;
            const visibleItems = group.items.filter((item) => {
              if (item.cap && !caps[item.cap]) return false;
              return true;
            });
            if (visibleItems.length === 0) return null;

            return (
              <div key={group.groupMs} style={{ marginBottom: '4px' }}>
                {!collapsed && (
                  <div
                    style={{
                      padding:       '12px 16px 4px',
                      fontSize:      '10px',
                      fontWeight:    700,
                      letterSpacing: '0.08em',
                      color:         C.gray600,
                      userSelect:    'none',
                    }}
                  >
                    {lang === 'en' ? group.groupEn : group.groupMs}
                  </div>
                )}
                {collapsed && <div style={{ height: '8px' }} />}

                {visibleItems.map((item) => {
                  const active = isActive(item.path);
                  return (
                    <NavItemButton
                      key={item.path}
                      item={item}
                      active={active}
                      collapsed={collapsed}
                      lang={lang}
                      onClick={() => handleNav(item.path)}
                    />
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* User section */}
        <div
          style={{
            borderTop: `1px solid ${C.sidebarBorder}`,
            padding:   collapsed ? '12px 0' : '12px 12px',
            flexShrink: 0,
          }}
        >
          {collapsed ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              <UserAvatar user={user} size={32} />
              <button
                onClick={onToggle}
                aria-label="Kembangkan bar sisi / Expand sidebar"
                style={iconButtonStyle}
              >
                <ChevronRight size={16} strokeWidth={2} />
              </button>
              <button
                onClick={onLogout}
                aria-label="Log keluar / Logout"
                style={{ ...iconButtonStyle, color: '#ef4444' }}
              >
                <LogOut size={16} strokeWidth={2} />
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <UserAvatar user={user} size={32} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    color:        C.white,
                    fontSize:     '13px',
                    fontWeight:   600,
                    overflow:     'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace:   'nowrap',
                  }}
                >
                  {user.email.split('@')[0]}
                </div>
                <div
                  style={{
                    fontSize:     '11px',
                    color:        C.yellow,
                    overflow:     'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace:   'nowrap',
                  }}
                >
                  {lang === 'en' ? (roleLabel?.en ?? primaryRoleLabel(user, lang)) : (roleLabel?.ms ?? primaryRoleLabel(user, lang))}
                </div>
              </div>
              <button
                onClick={onLogout}
                aria-label="Log keluar / Logout"
                style={{ ...iconButtonStyle, flexShrink: 0 }}
              >
                <LogOut size={15} strokeWidth={2} />
              </button>
            </div>
          )}
        </div>
      </nav>
    </>
  );
}

// ── Nav item button ───────────────────────────────────────────────────────────

function NavItemButton({
  item,
  active,
  collapsed,
  lang,
  onClick,
}: {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
  lang: string;
  onClick: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const label = lang === 'en' ? item.labelEn : item.labelMs;

  const style: React.CSSProperties = {
    display:        'flex',
    alignItems:     'center',
    gap:            '10px',
    width:          '100%',
    padding:        collapsed ? '10px 0' : '9px 16px',
    justifyContent: collapsed ? 'center' : 'flex-start',
    backgroundColor: active
      ? C.activeItemBg
      : hovered
      ? C.hoverItemBg
      : 'transparent',
    color: active ? C.yellow : hovered ? C.gray200 : C.gray400,
    border:         'none',
    cursor:         'pointer',
    fontFamily:     '"Inter", sans-serif',
    fontSize:       '13.5px',
    fontWeight:     active ? 600 : 400,
    transition:     'all 0.15s ease',
    textAlign:      'left',
    outline:        'none',
    position:       'relative',
    borderLeft:     active ? `3px solid ${C.yellow}` : '3px solid transparent',
  };

  return (
    <button
      style={style}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={collapsed ? label : undefined}
      aria-current={active ? 'page' : undefined}
    >
      <item.Icon size={17} strokeWidth={active ? 2.2 : 1.8} />
      {!collapsed && (
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
        </span>
      )}
    </button>
  );
}

// ── Top Navbar ────────────────────────────────────────────────────────────────

function Navbar({
  pageTitle,
  user,
  collapsed,
  onToggleMobile,
}: {
  pageTitle: string;
  user: AuthUser;
  collapsed: boolean;
  onToggleMobile: () => void;
}) {
  const { lang, setLang } = useLang();
  const sidebarW   = collapsed ? '64px' : '240px';
  const schoolId   = schoolIdFromUser(user);
  const role       = primaryRole(user);
  const roleLbl    = ROLE_LABELS[role];
  const caps       = getNavCapabilities(user);

  return (
    <header
      style={{
        position:        'fixed',
        top:             0,
        left:            sidebarW,
        right:           0,
        height:          '64px',
        backgroundColor: C.navbarBg,
        borderBottom:    `1px solid ${C.navbarBorder}`,
        display:         'flex',
        alignItems:      'center',
        padding:         '0 24px',
        zIndex:          200,
        transition:      'left 0.2s ease',
        gap:             '16px',
      }}
    >
      {/* Mobile hamburger */}
      <button
        onClick={onToggleMobile}
        aria-label="Buka navigasi / Open navigation"
        style={{
          ...iconButtonStyle,
          color:   C.gray600,
          display: 'none',
        }}
        className="mobile-menu-btn"
      >
        <Menu size={20} />
      </button>

      {/* Page title + read-only badge */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', minWidth: 0, gap: '8px' }}>
        <h1
          style={{
            margin:     0,
            fontSize:   '18px',
            fontWeight: 700,
            color:      C.black,
            fontFamily: '"Noto Sans", sans-serif',
            overflow:   'hidden',
            textOverflow: 'ellipsis',
            whiteSpace:   'nowrap',
          }}
        >
          {pageTitle}
        </h1>
        {/* 🔒 Read-only badge — visible to evaluator to confirm RBAC is active */}
        {caps.isReadOnly && (
          <span
            title={lang === 'en' ? 'View only — you do not have permission to edit' : 'Paparan sahaja — anda tidak mempunyai kebenaran untuk mengedit'}
            style={{
              display:        'inline-flex', alignItems: 'center', gap: '3px',
              fontSize:       '10px', fontWeight: 700,
              color:          '#6B7280', backgroundColor: '#F3F4F6',
              border:         '1px solid #E5E7EB', borderRadius: '999px',
              padding:        '2px 8px', whiteSpace: 'nowrap',
              cursor:         'help', userSelect: 'none', flexShrink: 0,
            }}
          >
            🔒 {lang === 'en' ? 'View only' : 'Paparan sahaja'}
          </span>
        )}
      </div>

      {/* School context pill — visible for school-scoped roles */}
      {schoolId && !caps.showMoeDashboard && (
        <div
          title={lang === 'en' ? 'Data isolation active — this school only' : 'Pengasingan data aktif — sekolah ini sahaja'}
          style={{
            display:        'flex', alignItems: 'center', gap: '4px',
            fontSize:       '11px', fontWeight: 600,
            color:          '#374151', backgroundColor: '#FFF8D6',
            border:         '1px solid #F7D000', borderRadius: '999px',
            padding:        '3px 10px', whiteSpace: 'nowrap',
            maxWidth:       '200px', overflow: 'hidden', textOverflow: 'ellipsis',
            cursor:         'help', flexShrink: 0,
          }}
        >
          📍 {lang === 'en' ? 'SMAS Ali' : 'SMAS Sayyidina Ali'}
        </div>
      )}

      {/* Role badge */}
      <div
        title={lang === 'en' ? `Signed in as: ${roleLbl?.en ?? role}` : `Log masuk sebagai: ${roleLbl?.ms ?? role}`}
        style={{
          display:        'flex', alignItems: 'center', gap: '4px',
          fontSize:       '11px', fontWeight: 700,
          color:          '#1A1A1A', backgroundColor: '#F7D000',
          borderRadius:   '999px', padding:   '3px 10px',
          whiteSpace:     'nowrap', flexShrink: 0, cursor: 'help',
        }}
      >
        {roleLbl?.badge ?? role}
      </div>

      {/* Language toggle */}
      <div
        role="group"
        aria-label="Language / Bahasa"
        style={{
          display:         'inline-flex',
          gap:             '2px',
          backgroundColor: C.gray100,
          borderRadius:    '999px',
          padding:         '3px',
          border:          `1px solid ${C.gray200}`,
        }}
      >
        {(['ms', 'en'] as const).map((l) => {
          const active = lang === l;
          return (
            <button
              key={l}
              onClick={() => setLang(l)}
              aria-pressed={active}
              title={l === 'ms' ? 'Bahasa Melayu' : 'English'}
              style={{
                padding:         '3px 12px',
                borderRadius:    '999px',
                border:          'none',
                backgroundColor: active ? C.yellow : 'transparent',
                color:           active ? C.black : C.gray600,
                fontSize:        '12px',
                fontWeight:      active ? 700 : 500,
                cursor:          'pointer',
                transition:      'all 0.15s',
                fontFamily:      '"Inter", sans-serif',
              }}
            >
              {l === 'ms' ? 'BM' : 'EN'}
            </button>
          );
        })}
      </div>

      {/* User avatar */}
      <UserAvatar user={user} size={36} />
    </header>
  );
}

// ── User avatar ───────────────────────────────────────────────────────────────

function UserAvatar({ user, size }: { user: AuthUser; size: number }) {
  const initials = user.email.slice(0, 2).toUpperCase();
  return (
    <div
      aria-hidden
      title={user.email}
      style={{
        width:           `${size}px`,
        height:          `${size}px`,
        borderRadius:    '50%',
        backgroundColor: C.yellow,
        color:           C.black,
        display:         'flex',
        alignItems:      'center',
        justifyContent:  'center',
        fontWeight:      700,
        fontSize:        `${Math.floor(size * 0.38)}px`,
        flexShrink:      0,
        userSelect:      'none',
      }}
    >
      {initials}
    </div>
  );
}

// ── Shared styles ─────────────────────────────────────────────────────────────

const iconButtonStyle: React.CSSProperties = {
  background:   'none',
  border:       'none',
  cursor:       'pointer',
  color:        C.gray400,
  display:      'flex',
  alignItems:   'center',
  justifyContent: 'center',
  padding:      '6px',
  borderRadius: '6px',
  transition:   'all 0.15s',
};

// ── AppShell ──────────────────────────────────────────────────────────────────

interface Props {
  user: AuthUser;
  onLogout: () => void;
  children: React.ReactNode;
}

export function AppShell({ user, onLogout, children }: Props) {
  const [collapsed,   setCollapsed]   = useState(false);
  const [mobileOpen,  setMobileOpen]  = useState(false);
  const [pageTitle,   setPageTitle]   = useState('Papan Pemuka / Dashboard');

  const handleLogout = useCallback(() => {
    clearTokens();
    onLogout();
  }, [onLogout]);

  const sidebarW = collapsed ? '64px' : '240px';

  return (
    <ShellContext.Provider value={{ setPageTitle, collapsed }}>
      {/* Inject minimal responsive CSS */}
      <style>{`
        @media (max-width: 768px) {
          .serps-sidebar { transform: translateX(-100%); width: 240px !important; }
          .serps-sidebar.mobile-open { transform: translateX(0) !important; }
          .serps-navbar { left: 0 !important; }
          .serps-content { margin-left: 0 !important; }
          .mobile-menu-btn { display: flex !important; }
        }
        * { box-sizing: border-box; }
        body { margin: 0; background: ${C.canvas}; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #ccc; border-radius: 3px; }
      `}</style>

      <Sidebar
        user={user}
        onLogout={handleLogout}
        collapsed={collapsed}
        onToggle={() => setCollapsed((c) => !c)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      <Navbar
        pageTitle={pageTitle}
        user={user}
        collapsed={collapsed}
        onToggleMobile={() => setMobileOpen((o) => !o)}
      />

      {/* Main content area */}
      <main
        style={{
          marginLeft:  sidebarW,
          paddingTop:  '64px',
          minHeight:   '100vh',
          backgroundColor: C.canvas,
          transition:  'margin-left 0.2s ease',
        }}
      >
        <div style={{ padding: '24px', maxWidth: '1280px' }}>
          {children}
        </div>
      </main>
    </ShellContext.Provider>
  );
}

// ── Page title hook ───────────────────────────────────────────────────────────

export function usePageTitle(title: string) {
  const { setPageTitle } = useShell();
  useEffect(() => {
    setPageTitle(title);
    return () => setPageTitle('SERPS');
  }, [title, setPageTitle]);
}
